/* eslint-disable react-hooks/exhaustive-deps */
// @ts-ignore - CSS imports are handled by the project build tooling, not TS declarations.
import './UnifiedChatList.css';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Button,
  InputBase,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  GroupOutlined,
  VisibilityOutlined,
  ChatBubbleOutlineOutlined,
} from '@mui/icons-material';
import { IconChat, IconClose } from 'components/studentActionBar/gmModal';
import { useAppSelector } from 'app/hooks';
import { useLocation, useHistory } from 'react-router-dom';
import { fetchUnifiedParticipants } from 'services/chatUnifiedService';
import {
  buildUnifiedParticipantLists,
  collectSessionIdsFromUnifiedData,
  fallbackUnreadFromIds,
  ChatParticipant,
} from './unifiedChatParticipantMapping';
import { useChatList } from './ChatListContext';
import { useChatBoxManager } from 'components/ChatBoxManager';
import websocketService from 'services/websocketService';
import { useUnreadCountsContext } from 'components/UnreadCountsContext';
import { routers } from 'router/router';
import { getUserId, getUserRole } from 'utils/sessionStorage';
import http from 'services/http';
import toast from 'react-hot-toast';
import { STRIPE_PRICE_NAME } from 'constants/common';

/** Chạy tối đa `chunkSize` tác vụ song song (join WS, v.v.) — tránh N lần await tuần tự. */
async function parallelChunk<T, R>(items: T[], chunkSize: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const out: R[] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize);
    const part = await Promise.all(chunk.map((item) => fn(item)));
    out.push(...part);
  }
  return out;
}

interface ChatSearchResult {
  message_id: number;
  content: string;
  conversation_id: string;
  created_at?: string;
}

interface UnifiedChatListProps {
  className?: string;
}

const UnifiedChatList: React.FC<UnifiedChatListProps> = ({ className }) => {
  const { isChatListVisible, hideChatList, chatListRefreshKey } = useChatList();
  const { openChatBox, isChatBoxOpen } = useChatBoxManager();
  const [counselors, setCounselors] = useState<ChatParticipant[]>([]);
  const [parents, setParents] = useState<ChatParticipant[]>([]);
  const [students, setStudents] = useState<ChatParticipant[]>([]);
  const [loadingCounselors, setLoadingCounselors] = useState(false);
  const [loadingParents, setLoadingParents] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  // const [selectedParticipant, setSelectedParticipant] = useState<ChatParticipant | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [searchResults, setSearchResults] = useState<ChatSearchResult[]>([]);
  const [searchCount, setSearchCount] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const location = useLocation();
  const history = useHistory();

  const basicInformation = useAppSelector((store) => store.basicInformation.myBasicInformation);
  const stripeSubscriptionInfo = useAppSelector((store) => store.basicInformation.stripeSubscriptionInfo);
  const stripeSubscriptionInfoLoaded = useAppSelector(
    (store) => store.basicInformation.stripeSubscriptionInfoLoaded
  );
  const userRole = basicInformation?.user_role?.toLowerCase() ?? '';
  /** Session often has role/user id before Redux hydrates — use for loaders + visibility. */
  const sessionRole = (getUserRole() || '').toLowerCase();
  const resolvedUserRole = userRole || sessionRole;
  /** Prefer sessionStorage when Redux has not hydrated yet (common right after login). */
  const userId = Number(getUserId()) || 0;
  const studentId = React.useMemo((): number | undefined => {
    const fromStore = basicInformation?.student_id ?? basicInformation?.id;
    if (fromStore != null && fromStore !== '') {
      const n = Number(fromStore);
      if (Number.isFinite(n) && n > 0) return n;
    }
    if (resolvedUserRole === 'student') {
      const sid = Number(getUserId());
      if (Number.isFinite(sid) && sid > 0) return sid;
    }
    return undefined;
  }, [basicInformation?.student_id, basicInformation?.id, resolvedUserRole]);

  const settingsActiveTab = React.useMemo(
    () => new URLSearchParams(location.search).get('active-tab'),
    [location.search]
  );
  const isOnSettingsInviteCounselorTab =
    location.pathname === routers.settings.path &&
    settingsActiveTab === '3' &&
    resolvedUserRole === 'student';
  const isOnSettingsInviteParentTab =
    location.pathname === routers.settings.path &&
    settingsActiveTab === '6' &&
    resolvedUserRole === 'student';
  /** Parent role: Settings tab 6 = Students (invite child). */
  const isOnSettingsInviteStudentTab =
    location.pathname === routers.settings.path &&
    settingsActiveTab === '6' &&
    resolvedUserRole === 'parent';

  // Extract student ID from URL for counselor/parent viewing specific student
  const urlStudentId = React.useMemo(() => {
    const match = location.pathname.match(/\/application\/(counselor|parent)\/(\d+)/);
    return match ? Number(match[2]) : null;
  }, [location.pathname]);

  // Hook for managing unread counts from context
  // Note: Unread counts are automatically fetched on mount and updated via WebSocket events
  const { getUnreadCount, fetchAllUnreadMessages } = useUnreadCountsContext();

  // Track joined rooms to avoid duplicate joins
  const joinedRoomsRef = useRef<Set<string>>(new Set());
  
  // Track urlStudentId to ensure we always use the latest value
  const urlStudentIdRef = useRef<number | null>(urlStudentId);
  const IGNORED_STUDENT_ID = 639;
  const IGNORED_STUDENT_EMAIL = 'student-demo@gradmap.com';
  const isIgnoredStudent = useCallback((id?: number, email?: string) => {
    return id === IGNORED_STUDENT_ID || email === IGNORED_STUDENT_EMAIL;
  }, []);

  // Update ref whenever urlStudentId changes
  useEffect(() => {
    urlStudentIdRef.current = urlStudentId;
  }, [urlStudentId]);

  const [chatTop, setChatTop] = useState<number | null>(null);

  useEffect(() => {
    if (!isChatListVisible) return;

    const placeBelowActionBar = () => {
      if (window.innerWidth <= 480) {
        setChatTop((prev) => (prev == null ? prev : null));
        return;
      }
      const bar = document.querySelector('.gm-student-bar');
      if (!bar) {
        setChatTop((prev) => (prev == null ? prev : null));
        return;
      }
      const next = Math.ceil(bar.getBoundingClientRect().bottom + 8);
      setChatTop((prev) => (prev === next ? prev : next));
    };

    placeBelowActionBar();
    window.addEventListener('resize', placeBelowActionBar);
    window.addEventListener('scroll', placeBelowActionBar, true);
    const observer = new ResizeObserver(placeBelowActionBar);
    const bar = document.querySelector('.gm-student-bar');
    if (bar) observer.observe(bar);
    const mutation = new MutationObserver(placeBelowActionBar);
    mutation.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('resize', placeBelowActionBar);
      window.removeEventListener('scroll', placeBelowActionBar, true);
      observer.disconnect();
      mutation.disconnect();
    };
  }, [isChatListVisible]);

  // Global chat search (header "Search people or messages...")
  useEffect(() => {
    const trimmed = searchQuery.trim();

    // Only call API when search open, we have a studentId, and there is a query
    if (!trimmed || !studentId) {
      if (!trimmed) {
        setSearchResults([]);
        setSearchCount(0);
      }
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const response: any = await http.post(
          '/api/partner/search-chat-notes',
          {
            studentId,
            search_query: trimmed,
          },
          { signal: controller.signal }
        );

        if (response && response.results) {
          setSearchResults(response.results);
          setSearchCount(response.count || response.results.length);
        } else {
          setSearchResults([]);
          setSearchCount(0);
        }
      } catch (error) {
        if ((error as any)?.name !== 'AbortError') {
          console.error('search-chat-notes error:', error);
        }
      } finally {
        setSearchLoading(false);
      }
    }, 500); // debounce 500ms

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [searchQuery, studentId]);

  /**
   * Helper function to ensure WebSocket connection and join room with retry logic
   * This ensures stable room joining by:
   * 1. Ensuring WebSocket is connected before joining
   * 2. Tracking joined rooms to avoid duplicates
   * 3. Retrying on failure with exponential backoff
   * 4. Handling connection state changes
   */
  const ensureJoinRoom = useCallback(
    async (
      sessionId: string,
      userId: number,
      userType: 'student' | 'counselor' | 'parent',
      maxRetries: number = 3,
      retryDelay: number = 1000
    ): Promise<boolean> => {
      // Check if already joined this room
      if (joinedRoomsRef.current.has(sessionId)) {
        return true;
      }

      let attempt = 0;
      while (attempt < maxRetries) {
        try {
          // Ensure WebSocket is connected
          if (!websocketService.isSocketConnected()) {
            await websocketService.connect();
          }

          // Double-check connection after connect attempt
          if (!websocketService.isSocketConnected()) {
            throw new Error('WebSocket connection failed');
          }

          // Join the room
          await websocketService.joinRoom({
            session_id: sessionId,
            user_id: userId,
            user_type: userType,
          });

          // Mark as joined
          joinedRoomsRef.current.add(sessionId);
          return true;
        } catch (error) {
          attempt++;
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          console.error(
            `Failed to join room ${sessionId} (attempt ${attempt}/${maxRetries}):`,
            errorMessage
          );

          if (attempt >= maxRetries) {
            console.error(`❌ Failed to join room ${sessionId} after ${maxRetries} attempts`);
            return false;
          }

          // Exponential backoff: wait before retrying
          const delay = retryDelay * Math.pow(2, attempt - 1);
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }

      return false;
    },
    []
  );

  // Get initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };


  const loadUnifiedChatParticipants = useCallback(async () => {
    const filterStudentId = urlStudentIdRef.current;
    if (!resolvedUserRole || !userId) return;
    if (resolvedUserRole === 'student' && !studentId) return;
    if (resolvedUserRole !== 'student' && resolvedUserRole !== 'counselor' && resolvedUserRole !== 'parent') {
      return;
    }

    const role = resolvedUserRole as 'student' | 'counselor' | 'parent';

    if (role === 'student') {
      setLoadingCounselors(true);
      setLoadingParents(true);
      setLoadingStudents(false);
    } else {
      setLoadingCounselors(true);
      setLoadingParents(true);
      setLoadingStudents(true);
    }

    try {
      const data = await fetchUnifiedParticipants({
        role,
        filter_student_id: filterStudentId,
      });

      if (!data) {
        setCounselors([]);
        setParents([]);
        setStudents([]);
        return;
      }

      const hints = data.meta?.session_hints ?? [];
      const built = buildUnifiedParticipantLists(role, data, {
        userId,
        studentId,
        hints,
        isIgnoredStudent,
      });

      const participantBySessionId = new Map<string, ChatParticipant>();
      [...built.counselors, ...built.parents, ...built.students].forEach((p) => {
        if (p.sessionId) participantBySessionId.set(p.sessionId, p);
      });

      /** Same rules as SimpleChatBox — do not join WS until student subscription is known (avoids race with undefined plan). */
      const shouldJoinWebSocketRoom = (sessionId: string): boolean => {
        // if (role === 'student') {
        //   if (!stripeSubscriptionInfoLoaded) return false;
        //   return stripeSubscriptionInfo?.subcription_plan !== STRIPE_PRICE_NAME.GradMapStarter;
        // }
        // const p = participantBySessionId.get(sessionId);
        // if (!p) return true;
        // const plan = p.subscriptionPlan;
        // return Boolean(plan && plan !== STRIPE_PRICE_NAME.GradMapStarter);
        return true
      };

      // Join WS rooms: meta hints + optional per-row `session_hint` (same session ids as send/join).
      const allSessionIds = collectSessionIdsFromUnifiedData(data);
      const sessionIdsToJoin = allSessionIds.filter(shouldJoinWebSocketRoom);

      const JOIN_CONCURRENCY = 8;
      if (sessionIdsToJoin.length > 0) {
        await parallelChunk(sessionIdsToJoin, JOIN_CONCURRENCY, async (sid) => {
          await ensureJoinRoom(sid, userId, role);
        });
      }

      const joinedOk = (p: ChatParticipant) => {
        if (!p.sessionId) return true;
        if (joinedRoomsRef.current.has(p.sessionId)) return true;
        if (role === 'student' && !stripeSubscriptionInfoLoaded) return true;
        if (role === 'student' && stripeSubscriptionInfo?.subcription_plan === STRIPE_PRICE_NAME.GradMapStarter) {
          return true;
        }
        if (role !== 'student') {
          const plan = p.subscriptionPlan;
          if (!plan || plan === STRIPE_PRICE_NAME.GradMapStarter) return true;
        }
        return false;
      };

      const nextCounselors = [...built.counselors.filter(joinedOk)];
      const nextParents = [...built.parents.filter(joinedOk)];
      const nextStudents = [...built.students.filter(joinedOk)];

      setCounselors(nextCounselors);
      setParents(nextParents);
      setStudents(nextStudents);

      const fromIds = Array.isArray(data.meta?.from_ids_for_unread)
        ? data.meta!.from_ids_for_unread!
        : fallbackUnreadFromIds(role, nextCounselors, nextParents, nextStudents);
      if (fromIds.length > 0) {
        fetchAllUnreadMessages(fromIds);
      }
    } catch (error) {
      console.error('Error loading unified chat participants:', error);
      setCounselors([]);
      setParents([]);
      setStudents([]);
    } finally {
      setLoadingCounselors(false);
      setLoadingParents(false);
      setLoadingStudents(false);
    }
  }, [
    resolvedUserRole,
    userId,
    studentId,
    ensureJoinRoom,
    fetchAllUnreadMessages,
    isIgnoredStudent,
    stripeSubscriptionInfo?.subcription_plan,
    stripeSubscriptionInfoLoaded,
  ]);

  useEffect(() => {
    if (!resolvedUserRole || !userId || (resolvedUserRole === 'student' && !studentId)) {
      return;
    }

    const isDashboardOrApp =
      location.pathname.includes('/dashboard') ||
      location.pathname.includes('/application') ||
      location.pathname.includes('/settings') ||
      location.pathname.includes('/support');

    if (!isDashboardOrApp) {
      return;
    }

    void loadUnifiedChatParticipants();
  }, [
    resolvedUserRole,
    userId,
    studentId,
    location.pathname,
    urlStudentId,
    loadUnifiedChatParticipants,
  ]);

  useEffect(() => {
    if (!chatListRefreshKey) return;
    if (!resolvedUserRole || !userId || (resolvedUserRole === 'student' && !studentId)) return;

    const isDashboardOrApp =
      location.pathname.includes('/dashboard') ||
      location.pathname.includes('/application') ||
      location.pathname.includes('/settings') ||
      location.pathname.includes('/support');
    if (!isDashboardOrApp) return;

    void loadUnifiedChatParticipants();
  }, [
    chatListRefreshKey,
    resolvedUserRole,
    userId,
    studentId,
    location.pathname,
    loadUnifiedChatParticipants,
  ]);

  // Rejoin rooms when WebSocket reconnects
  useEffect(() => {
    const socket = websocketService.getSocket();
    if (!socket) return;

    const handleReconnect = async () => {
      // Clear joined rooms tracking to allow rejoin
      const roomsToRejoin = Array.from(joinedRoomsRef.current);
      joinedRoomsRef.current.clear();

      // Rejoin all rooms sequentially
      for (const sessionId of roomsToRejoin) {
        // Extract userId and userType from sessionId format: `${userId}-${otherId}` or `${otherId}-${userId}`
        const parts = sessionId.split('-');
        if (parts.length === 2) {
          // const [id1, id2] = parts.map(Number);
          // Determine which ID is the current user's ID
          const currentUserId = userId;
          const userType = resolvedUserRole as 'student' | 'counselor' | 'parent';

          // Rejoin the room
          await ensureJoinRoom(sessionId, currentUserId, userType, 2, 500);
        }
      }
    };

    socket.on('reconnect', handleReconnect);

    return () => {
      socket.off('reconnect', handleReconnect);
    };
  }, [userId, resolvedUserRole, ensureJoinRoom]);

  // Cleanup: Clear joined rooms when user changes or component unmounts
  useEffect(() => {
    return () => {
      // Clear joined rooms tracking on unmount or user change
      joinedRoomsRef.current.clear();
    };
  }, [userId, resolvedUserRole]);

  // Re-fetch participants when a new unread message arrives so lastMessagePreview stays current
  useEffect(() => {
    const socket = websocketService.getSocket();
    if (!socket || !userId) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const handleNewMessage = (message: any) => {
      if (
        message.conversation_id &&
        (Number(message.from_id) === userId || Number(message.to_id) === userId)
      ) {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          void loadUnifiedChatParticipants();
        }, 500);
      }
    };

    socket.on('chat:new_message', handleNewMessage);
    return () => {
      socket.off('chat:new_message', handleNewMessage);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [userId, loadUnifiedChatParticipants]);

  const handleParticipantClick = (participant: ChatParticipant) => {
    // setSelectedParticipant(participant);

    // Check if chatbox already open for this participant
    if (isChatBoxOpen(participant.id)) {
      // If already open, just close the chat list
      hideChatList();
      return;
    }

    // Open new chatbox (max 3, handled in ChatBoxManager)
    openChatBox(participant);

    // Close chat list when clicking on a participant
    hideChatList();
  };

  // Find participant by conversation_id (e.g. "session_633-542")
  const findParticipantByConversationId = (conversationId: string): ChatParticipant | null => {
    const sessionId = conversationId.startsWith('session_')
      ? conversationId.replace('session_', '')
      : conversationId;

    const allParticipants = [...students, ...counselors, ...parents];
    return allParticipants.find((p) => p.sessionId === sessionId) || null;
  };

  // Highlight helper for search text
  const renderHighlightedText = (text: string) => {
    const query = searchQuery.trim();
    if (!query) return text;

    try {
      const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escaped})`, 'gi');
      const parts = text.split(regex);

      if (parts.length === 1) return text;

      return (
        <>
          {parts.map((part, index) =>
            part.toLowerCase() === query.toLowerCase() ? (
              <span key={index} className="search-text-highlight">
                {part}
              </span>
            ) : (
              <span key={index}>{part}</span>
            )
          )}
        </>
      );
    } catch {
      return text;
    }
  };

  const formatMessageTime = (timestamp?: string) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const renderParticipantList = (
    participants: ChatParticipant[],
    title: string,
    emptyMessage: string,
    isLoading: boolean
  ) => {
    // Show loading if still loading
    if (isLoading) {
      return (
        <Box
          className="loading-container"
          sx={{ display: 'flex', justifyContent: 'center', py: 3 }}
        >
          <CircularProgress size={24} />
        </Box>
      );
    }

    // Only show empty message if loading is done and no participants
    if (participants.length === 0) {
      return (
        <Box className="empty-list">
          <Typography variant="body2" color="text.secondary" className="empty-text">
            {emptyMessage}
          </Typography>
        </Box>
      );
    }

    return (
      <List className="participant-list">
        {participants.map((participant) => {
          const conversationId = `session_${participant.sessionId}`;
          const unreadCount = conversationId ? getUnreadCount(conversationId) : 0;

          return (
            <ListItem key={participant.id} disablePadding>
              <ListItemButton
                className="participant-item"
                onClick={() => handleParticipantClick(participant)}
              >
                <ListItemAvatar>
                  <Avatar className={`${participant.type}-avatar`}>
                    {participant.avatar ? (
                      <img src={participant.avatar} alt={participant.name} />
                    ) : (
                      getInitials(participant.name)
                    )}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={participant.name}
                  secondary={
                    participant.type === 'student' && participant.yearFinishHighSchool ? (
                      `Class of ${participant.yearFinishHighSchool}`
                    ) : participant.lastMessagePreview ? (
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {participant.lastMessageSentBy === userId ? 'You' : participant.name}
                        {': '}
                        {participant.lastMessagePreview}
                      </Typography>
                    ) : participant.type === 'student' ? (
                      'Class of N/A'
                    ) : participant.studentInfo ? (
                      <span>
                        {participant.relationship && `${participant.relationship} of `}
                        <strong>{participant.studentInfo.name}</strong>
                        {participant.studentInfo.yearFinishHighSchool &&
                          ` (Class of ${participant.studentInfo.yearFinishHighSchool})`}
                      </span>
                    ) : (
                      participant.relationship || participant.email || participant.type
                    )
                  }
                />
                {unreadCount > 0 && <span className="chat-unread-badge">{unreadCount}</span>}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    );
  };

  if (
    !resolvedUserRole ||
    (resolvedUserRole !== 'student' &&
      resolvedUserRole !== 'counselor' &&
      resolvedUserRole !== 'parent')
  ) {
    return null;
  }

  if (!isChatListVisible) {
    return null;
  }

  const query = searchQuery.trim().toLowerCase();
  const filterPeople = (list: ChatParticipant[]) => {
    if (!query) return list;
    return list.filter((person) =>
      `${person.name || ''} ${person.lastMessagePreview || ''} ${person.email || ''}`.toLowerCase().includes(query),
    );
  };
  const visibleStudents = filterPeople(students);
  const visibleCounselors = filterPeople(counselors);
  const visibleParents = filterPeople(parents);

  return (
    <div
      className={`unified-chat-list ${className || ''}`}
      style={
        chatTop != null
          ? {
              top: chatTop,
              height: `min(520px, calc(100vh - ${chatTop + 16}px))`,
              maxHeight: `min(520px, calc(100vh - ${chatTop + 16}px))`,
            }
          : undefined
      }
    >
      <div className="chat-list-header">
        <Typography variant="h6" className="chat-list-title">
          <IconChat />
          Messages
        </Typography>
        <button type="button" className="chat-panel-close" aria-label="Close messages" onClick={hideChatList}>
          <IconClose />
        </button>
      </div>
      <div className="chat-list-search">
        <div className="chat-search-input-wrapper">
          <SearchIcon className="chat-search-icon" fontSize="small" />
          <InputBase
            className="chat-search-input"
            placeholder="Search people or messages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            inputRef={searchInputRef}
            inputProps={{ 'aria-label': 'Search people or messages' }}
          />
          {searchQuery && (
            <IconButton size="small" className="chat-search-close" aria-label="Clear search" onClick={() => setSearchQuery('')}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      </div>

      <div className="chat-list-content">
        {/* Search results list */}
        {searchQuery.trim() && (
          <Paper className="chat-search-results" elevation={0}>
            <div className="section-header">
              <Typography variant="subtitle2" className="section-title">
                Search results ({searchCount})
              </Typography>
            </div>
            {searchLoading ? (
              <Box
                className="loading-container"
                sx={{ display: 'flex', justifyContent: 'center', py: 2 }}
              >
                <CircularProgress size={20} />
              </Box>
            ) : (() => {
              const resultsWithParticipant = searchResults
                .map((result) => ({
                  result,
                  participant: findParticipantByConversationId(result.conversation_id),
                }))
                .filter(({ participant }) => participant !== null) as { result: ChatSearchResult; participant: ChatParticipant }[];
              return resultsWithParticipant.length === 0 ? (
                <Box className="empty-list">
                  <Typography variant="body2" color="text.secondary" className="empty-text">
                    No messages found
                  </Typography>
                </Box>
              ) : (
              <List className="participant-list">
                {resultsWithParticipant.map(({ result, participant }) => (
                    <ListItem
                      key={result.message_id}
                      disablePadding
                      onClick={() =>
                        handleParticipantClick({
                          ...participant,
                          initialMessageId: result.message_id,
                        })
                      }
                    >
                      <ListItemButton className="participant-item">
                        <ListItemAvatar>
                          <Avatar className={`${participant.type}-avatar`}>
                            {participant.avatar ? (
                              <img src={participant.avatar} alt={participant.name} />
                            ) : (
                              getInitials(participant.name)
                            )}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <span className="search-result-title">
                              {participant.name}{' '}
                              <Typography
                                variant="caption"
                                component="span"
                                color="text.secondary"
                              >
                                {formatMessageTime(result.created_at)}
                              </Typography>
                            </span>
                          }
                          secondary={
                            <span className="search-result-content">
                              {renderHighlightedText(result.content || '')}
                            </span>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
              </List>
              );
            })()}
          </Paper>
        )}

        {/* When searching with results, hide the normal lists */}
        <>
            {/* Students Section (for counselor and parent) */}
            {(resolvedUserRole === 'counselor' || resolvedUserRole === 'parent') && (
          <>
            <Paper className="chat-section" elevation={0}>
              <div className="section-header">
                <Typography variant="h6" className="section-title">
                  STUDENTS
                </Typography>
                {resolvedUserRole === 'parent' && (
                  <Button
                    size="small"
                    className="invite-counselor-btn"
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      if (isOnSettingsInviteStudentTab) {
                        hideChatList();
                        toast('You are already in the Invite Student page');
                        return;
                      }
                      hideChatList();
                      history.push(`${routers.settings.path}?active-tab=6`);
                    }}
                  >
                    Invite Student
                  </Button>
                )}
              </div>

              {renderParticipantList(
                visibleStudents,
                'Students',
                query && students.length ? 'No conversations match that search.' : 'No student conversations yet',
                loadingStudents
              )}
            </Paper>
          </>
            )}

            {/* Counselors Section (for student and parent) */}
            {(resolvedUserRole === 'student' || resolvedUserRole === 'parent') && (
          <>
            <Paper className="chat-section" elevation={0}>
              <div className="section-header">
                <Typography variant="h6" className="section-title">
                  COUNSELORS
                </Typography>
                {resolvedUserRole === 'student' && (
                  <Button
                    size="small"
                    className="invite-counselor-btn"
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      if (isOnSettingsInviteCounselorTab) {
                        hideChatList();
                        toast('You are already in the Invite Counselor page');
                        return;
                      }
                      hideChatList();
                      history.push(`${routers.settings.path}?active-tab=3`);
                    }}
                  >
                    Invite Counselor
                  </Button>
                )}
              </div>
              {renderParticipantList(
                visibleCounselors,
                'Counselors',
                query && counselors.length ? 'No conversations match that search.' : 'No counselor conversations yet',
                loadingCounselors
              )}
            </Paper>
          </>
            )}

            {/* Parents Section (for student and counselor) */}
            {(resolvedUserRole === 'student' || resolvedUserRole === 'counselor') && (
          <>
            <Paper className="chat-section" elevation={0}>
              <div className="section-header">
                <Typography variant="h6" className="section-title">
                  PARENTS
                </Typography>
              </div>
              {resolvedUserRole === 'student' && parents.length === 0 && !loadingParents && !query ? (
                <Box className="invite-parent-card">
                  <div className="invite-parent-icon">
                    <GroupOutlined sx={{ fontSize: 28, color: '#3b82f6' }} />
                  </div>
                  <Typography variant="subtitle1" className="invite-parent-title">
                    Keep your parent in the loop
                  </Typography>
                  <div className="invite-parent-features">
                    <div className="invite-parent-feature">
                      <VisibilityOutlined sx={{ fontSize: 18, color: '#3b82f6' }} />
                      <span>They can see your progress</span>
                    </div>
                    <div className="invite-parent-feature">
                      <ChatBubbleOutlineOutlined sx={{ fontSize: 18, color: '#3b82f6' }} />
                      <span>Communicate with your counselor</span>
                    </div>
                  </div>
                  <Button
                    variant="contained"
                    color="primary"
                    className="invite-parent-button"
                    onClick={() => {
                      if (isOnSettingsInviteParentTab) {
                        hideChatList();
                        toast('You are already in the Invite Parent page');
                        return;
                      }
                      hideChatList();
                      history.push(`${routers.settings.path}?active-tab=6`);
                    }}
                  >
                    Invite Parent
                  </Button>
                </Box>
              ) : (
                renderParticipantList(
                  visibleParents,
                  'Parents',
                  query && parents.length ? 'No conversations match that search.' : 'No parent conversations yet',
                  loadingParents
                )
              )}
            </Paper>
          </>
            )}
          </>
      </div>
    </div>
  );
};

export default UnifiedChatList;
